import { useContext, useLayoutEffect, useMemo, useState } from "react";
import { BulkUpdateBotStep } from "@shared/features/bots/types";
import pluralize from "pluralize";
import {
  BOT_TYPE_TO_DISPLAY_NAME,
  BULK_UPDATE_BOTS_MUTATION_KEY,
} from "@shared/features/bots/constants";
import {
  BasicBotData,
  BotData,
  BotStatus,
  BotType,
  BulkChangeBotsStatusRequest,
  DisplayResultMessage,
  LanguageNameSpaces,
} from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import { useInvalidAccount } from "@shared/features/settings/hooks";
import { MyBotsPageContext } from "@shared/features/bots/MyBotsPage.context";
import { useBranding } from "@shared/utils/hooks/useBranding";
import { useSymbolsMetadata } from "@shared/utils/hooks/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { JobToastType } from "@shared/slices/jobs.slice";
import { useAddJobToast } from "@shared/utils/hooks/useAddJobToast";

export interface UseBulkUpdateBotsModalProps {
  open?: boolean;
  botsIDs?: number[];
  closeModal: () => void;
  isDeactivate?: boolean;
  onFinish?: () => void;
  displayResultMessage: DisplayResultMessage;
  botType: BotType;
  botIDToBotData: Record<number, BotData | BasicBotData>;
}

const useBulkUpdateBotsModal = ({
  open,
  botsIDs,
  closeModal,
  isDeactivate,
  onFinish,
  displayResultMessage,
  botType,
  botIDToBotData,
}: UseBulkUpdateBotsModalProps) => {
  const { isChannelConfiguration, getBotsDataStatus } =
    useContext(MyBotsPageContext) || {};
  const { getInvalidAccountData } = useInvalidAccount();
  const { brandingData } = useBranding();
  const isGridBots = botType === BotType.GRID;

  const numOpenTrades = useMemo(
    () =>
      botsIDs?.reduce((acc, botID) => {
        const bot = botIDToBotData[botID];
        if (bot) {
          return acc + bot.number_of_open_trades;
        } else return acc;
      }, 0),
    [botsIDs, botIDToBotData]
  );
  const hasOpenTrades = !!numOpenTrades;

  const hasCoins = useMemo(
    () =>
      isGridBots &&
      hasOpenTrades &&
      botsIDs?.some((botID) => {
        const bot = botIDToBotData[botID];
        return bot?.active_trade?.invested_quantity;
      }),
    [botsIDs, botIDToBotData]
  );

  const numInvalidAccounts = useMemo(() => {
    return (
      botsIDs?.filter((botID) => {
        const bot = botIDToBotData[botID];
        const accountData = getInvalidAccountData(bot?.client_id);
        return accountData?.isInvalid || accountData?.isInactive;
      }).length || 0
    );
  }, [botsIDs, botIDToBotData]);

  const getDefaultStep = () => {
    if (isDeactivate && hasOpenTrades && !isGridBots) {
      return BulkUpdateBotStep.CLOSE_TRADES;
    } else {
      return BulkUpdateBotStep.CONFIRM;
    }
  };

  const [step, setStep] = useState(getDefaultStep);
  const { addJobToast, removeJobToast } = useAddJobToast(JobToastType.Bot);
  const [bulkChangeBotsStatus, bulkChangeBotsStatusStatus] =
    sharedLib.sharedApi.bots.useBulkChangeBotsStatusMutation({
      fixedCacheKey: BULK_UPDATE_BOTS_MUTATION_KEY,
    });
  const [bulkChangeBotsStatusJob, bulkChangeBotsStatusJobStatus] =
    sharedLib.sharedApi.bots.useBulkChangeBotsStatusJobMutation({
      fixedCacheKey: BULK_UPDATE_BOTS_MUTATION_KEY + "_job",
    });
  const isLoading = isDeactivate
    ? bulkChangeBotsStatusJobStatus.isLoading
    : bulkChangeBotsStatusStatus.isLoading;
  const { isSymbolActiveInAccount } = useSymbolsMetadata();
  const [closeOpenTrades, setCloseOpenTrades] = useState(true);
  const singleBot =
    botsIDs?.length === 1 ? botIDToBotData[botsIDs[0]] : undefined;
  const isSignalsBot = botType === BotType.SIGNAL;
  const isInactiveSymbolBot =
    !isSignalsBot &&
    !!singleBot &&
    !isSymbolActiveInAccount(singleBot?.symbols?.[0], singleBot?.client_id);
  const [sellCoins, setSellCoins] = useState(true);
  const showExistingCoinsOptions =
    (isInactiveSymbolBot && isDeactivate && hasOpenTrades && closeOpenTrades) ||
    (isGridBots && isDeactivate && hasOpenTrades && hasCoins);

  useLayoutEffect(() => {
    setSellCoins(!isInactiveSymbolBot);
  }, [isInactiveSymbolBot]);

  useLayoutEffect(() => {
    if (open) {
      setStep(getDefaultStep());
    }
  }, [open]);

  const onChangeBotsStatus = () => {
    if (botsIDs) {
      const args: BulkChangeBotsStatusRequest = {
        bot_ids: botsIDs,
        is_active: !isDeactivate,
        bot_type: botType,
      };
      if (isDeactivate) {
        args.should_cancel_trade = closeOpenTrades;
      }
      if (showExistingCoinsOptions) {
        args.ratio = sellCoins ? 100 : 0;
      }
      if (isDeactivate && botsIDs.length > 1) {
        const id = addJobToast(args);
        onCloseModal();
        bulkChangeBotsStatusJob(args)
          .unwrap()
          .catch(() => {})
          .finally(() => {
            setTimeout(() => onFinish?.(), 150);
            removeJobToast(id);
          });
      } else {
        displayResultMessage(
          bulkChangeBotsStatus(args).unwrap(),
          `Bots successfully activated.`,
          `Failed to activate bots.`
        )
          .finally(() => {
            onCloseModal();
            setTimeout(() => onFinish?.(), 150);
          })
          .catch(() => {});
      }
    }
  };

  const onCloseModal = () => {
    closeModal();
    setTimeout(() => {
      setStep(BulkUpdateBotStep.CLOSE_TRADES);
      setCloseOpenTrades(true);
    }, 150);
  };

  const onOkPress = () => {
    if (step === BulkUpdateBotStep.CLOSE_TRADES) {
      if (numInvalidAccounts && closeOpenTrades)
        setStep(BulkUpdateBotStep.CONFIRM_INACTIVE_ACCOUNT);
      else setStep(BulkUpdateBotStep.CONFIRM);
    } else if (step === BulkUpdateBotStep.CONFIRM_INACTIVE_ACCOUNT) {
      onChangeBotsStatus();
    } else if (step === BulkUpdateBotStep.CONFIRM) {
      onChangeBotsStatus();
    }
  };

  let actionName = isDeactivate ? "Deactivate" : "Activate";
  if (isChannelConfiguration)
    actionName = isDeactivate ? "Unpublish" : "Publish";
  const numBots = botsIDs?.length || 0;
  const botText = isChannelConfiguration
    ? "Configuration"
    : pluralize("Bot", numBots);
  const tradesText = pluralize("trade", numBots > 1 ? numBots : numOpenTrades);

  let confirmStepPrefixText = "";
  const showDCATradesWarning =
    botType === BotType.ASAP &&
    (!isDeactivate || (hasOpenTrades && closeOpenTrades));
  const showTradingViewTradesWarning =
    botType === BotType.TRADING_VIEW &&
    isDeactivate &&
    hasOpenTrades &&
    closeOpenTrades;
  if (showExistingCoinsOptions) {
    const botText =
      botsIDs?.length === 1
        ? `your bot's open ${tradesText}`
        : "all the deactivated bots' open trades";
    confirmStepPrefixText = `This action will close ${botText}. What do you want to do with the existing coins?`;
  } else if (showDCATradesWarning || showTradingViewTradesWarning) {
    const botText =
      botsIDs?.length === 1
        ? `the bot's ${tradesText}`
        : `all the ${isDeactivate ? "deactivated" : "activated"} bots' trades`;
    confirmStepPrefixText = `Please note that ${botText} will be immediately ${
      isDeactivate ? "closed" : "opened"
    }.`;
  }

  let confirmationText: string[] =
    isSignalsBot &&
    brandingData?.modified_create_bot_button_texts &&
    !isDeactivate
      ? [
          `Activating ${pluralize("this", botsIDs?.length)} ${pluralize(
            "bot",
            botsIDs?.length
          )} will automatically open new trades on your account every time a new signal is published, using your saved configuration. You can deactivate the ${pluralize(
            "bot",
            botsIDs?.length
          )} at any time.`,
        ]
      : ["Are you sure you wish to continue?"];
  if (isChannelConfiguration) {
    const bots = getBotsDataStatus?.data?.data;

    if (isDeactivate) {
      confirmationText = [
        "Unpublishing this configuration will leave the group without a default configuration.",
        "If you wish to replace the default configuration, publish another configuration instead.",
        confirmationText[0],
      ];
    } else {
      const hasAnotherActiveBot = (bots as BotData[])?.find(
        (bot) => bot.id !== botsIDs?.[0] && bot.status === BotStatus.ACTIVE
      );
      if (hasAnotherActiveBot) {
        confirmationText = [
          "This configuration will replace another active configuration as the group default.",
          confirmationText[0],
        ];
      }
    }
  }

  const closeTradesOptionsData = {
    mainText: `What do you want to do with the ${
      numBots === 1 ? "bot's" : "bots'"
    } open ${tradesText}?`,
    options: [
      { text: `Close open ${tradesText}`, value: true },
      { text: `Keep ${tradesText} open`, value: false },
    ],
  };

  const getOkText = () => {
    return `${
      step === BulkUpdateBotStep.CONFIRM && !showExistingCoinsOptions
        ? "Yes, "
        : ""
    }${actionName} ${botText}`;
  };

  const getCancelText = () => {
    return step === BulkUpdateBotStep.CONFIRM && !showExistingCoinsOptions
      ? "No, go back"
      : "Cancel";
  };

  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  return {
    onOkPress,
    onCloseModal,
    titleText: `${actionName}${
      botsIDs?.length === 1 ? "" : ` ${botsIDs?.length}`
    } ${
      isChannelConfiguration ? "" : `${t(BOT_TYPE_TO_DISPLAY_NAME[botType])} `
    }${botText}`,
    okText: getOkText(),
    cancelText: getCancelText(),
    isLoading,
    closeOpenTrades,
    setCloseOpenTrades,
    step,
    confirmStepPrefixText,
    closeTradesOptionsData,
    sellCoins,
    setSellCoins,
    showExistingCoinsOptions,
    isInactiveSymbolBot,
    numInvalidAccounts,
    confirmationText,
  };
};

export default useBulkUpdateBotsModal;
